import React, { useState } from "react";
import styled from "styled-components";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";

const Note = styled.p`
  font-size: 14px;
  color: ${({ colors }) => colors.labelText};
  font-weight: bold;
  margin-bottom: 20px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FileInput = styled.input``;

const SubmitButton = styled.button`
  width: fit-content;
  padding: 8px 16px;
  background-color: ${({ colors }) => colors.buttonBackground};
  color: white;
  border: none;
  border-radius: 5px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: ${({ colors }) => colors.buttonHoverBackground};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageContainer = styled.div`
  width: 100%;
`;

export default function ImportInvoicesContent({ colors }) {
  const [file, setFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultTitle, setResultTitle] = useState("");
  const [resultMessage, setResultMessage] = useState("");
  const [resultStatus, setResultStatus] = useState("Success");

  const handleFileChange = (e) => setFile(e.target.files[0]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (file) setConfirmOpen(true);
  };

  // Read the CSV file as plain text
  const readFileText = (file) =>
    new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result);
      reader.onerror = () => rej(reader.error);
      reader.readAsText(file);
    });

  // Split on commas not inside quotes, then strip wrapping quotes
  const splitCsvLine = (line) =>
    line
      .split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/)
      .map((s) => s.replace(/^"|"$/g, ""));

  // Normalize a date string into "YYYY-MM-DD" form, or leave if already in that form.
  const fmtDate = (d) => {
    if (!d) return "";
    // If it already looks like YYYY-MM-DD, assume normalized.
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      return d;
    }
    // If it contains slashes, convert M/D/YYYY or MM/DD/YYYY → YYYY-MM-DD
    if (d.includes("/")) {
      const [m, day, y] = d.split("/");
      const mm = m.padStart(2, "0");
      const dd = day.padStart(2, "0");
      return `${y}-${mm}-${dd}`;
    }
    // Otherwise assume it's some other format but leave it as-is
    return d;
  };

  // Normalize phone: if already digits-only, return as-is; else strip non-digits
  const fmtPhone = (p) => {
    if (!p) return "";
    // If already all digits, no change
    if (/^\d+$/.test(p)) {
      return p;
    }
    return p.replace(/[^\d]/g, "");
  };

  // Parse entire CSV text into JSON structure
  const parseCsvToJson = (text) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) {
      return { invoices: [] };
    }

    const headers = splitCsvLine(lines[0]);
    const invoices = lines.slice(1).map((line) => {
      const cols = splitCsvLine(line);
      const row = headers.reduce((acc, h, i) => {
        acc[h.trim()] = cols[i] || "";
        return acc;
      }, {});

      return {
        purchase_date:    fmtDate(row["Purchase Date"] || row["purchase_date"]),
        start_date:       fmtDate(row["Start Date"] || row["start_date"]),
        end_date:         fmtDate(row["End Date"] || row["end_date"]),
        customer_code:    row["Customer ID"] || row["customer_code"] || "",
        invoice_number:   row["Invoice Number"] || row["invoice_number"] || "",
        org_name:         row["Customer Name"] || row["org_name"] || "",
        contact_phone:    fmtPhone(row["Phone"] || row["contact_phone"] || ""),
        org_street:       row["Address"] || row["org_street"] || "",
        org_city:         row["City"] || row["org_city"] || "",
        org_state:        row["State"] || row["org_state"] || "",
        org_zip:          row["Zip"] || row["org_zip"] || "",
        org_country:      row["Country"] || row["org_country"] || "",
        contact_name:     row["Contact Person"] || row["contact_name"] || "",
        po_number:        row["PO Number"] || row["po_number"] || "",
        item_description:
          row["Product ID"] || row["item_description"] || "",
        quantity:
          Number(row["QTY"] || row["quantity"]) || 0,
        invoice_amount:
          Number(row["Total Amount"] || row["invoice_amount"]) || 0,
        customer_balance:
          row["Customer Balance"] || row["customer_balance"] || "",
        salesperson_tag:
          row["Salesperson ID"] || row["salesperson_tag"] || "",
        item_number:
          row["Item Number"] || row["item_number"] || "",
        contact_email:
          row["Contact Email"] || row["contact_email"] || "",
        number_of_schools:
          Number(row["Schools"] || row["number_of_schools"]) || 0,
        number_of_users:
          Number(row["Students"] || row["number_of_users"]) || 0,
        customer_tag:   row["customer_tag"] || "",
        unique_id:      row["unique_id"] || "",
        parent_code:    row["parent_code"] || "",
        comments:       row["comments"] || ""
      };
    });

    return { invoices };
  };

  const doImport = async () => {
    setConfirmOpen(false);
    setProcessing(true);
    setResultStatus("Success");

    try {
      const csvText = await readFileText(file);
      const payload = parseCsvToJson(csvText);
      const resp = await API.importInvoice(payload);
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setResultTitle(status === "Failed" ? "Error" : "Success");
      setResultMessage(resp.message || (status === "Failed" ? "Import failed." : "Invoices imported successfully."));
      setResultStatus(status);
    } catch (err) {
      setResultTitle("Error");
      setResultMessage(err.message || "Import failed.");
      setResultStatus("Failed");
    } finally {
      setProcessing(false);
      setResultOpen(true);
    }
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setResultStatus("Success");
  };

  return (
    <PageContainer>
      <h1 style={{ fontSize: "24px", color: colors.labelText, fontWeight:"normal" }}>Import Invoices</h1>
      <Note colors={colors}>
        Note: Beginning this process will lock the Import Invoice system for all other admins until it is complete or canceled.
      </Note>

      <Form onSubmit={handleSubmit}>
        <label htmlFor="invoiceFile" style={{ color: colors.labelText }}>
          Select CSV file:
        </label>
        <FileInput
          id="invoiceFile"
          type="file"
          accept=".csv"
          onChange={handleFileChange}
        />

        <div style={{ display: "flex", alignItems: "center" }}>
          <SubmitButton
            colors={colors}
            type="submit"
            disabled={!file || processing}
          >
            {processing ? "Importing…" : "Import"}
          </SubmitButton>
          <span style={{ marginLeft: "8px" }}>to upload the file!</span>
        </div>
      </Form>

      {/* Lock Confirmation */}
      <ConfirmationModal
        show={confirmOpen}
        title="Please Confirm"
        message="Starting this import will lock the system for all other admins. Continue?"
        onConfirm={doImport}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* Result Dialog */}
      <ConfirmationModal
        show={resultOpen}
        title={resultTitle}
        message={resultMessage}
        onConfirm={handleResultClose}
        onCancel={handleResultClose}
        status={resultStatus}
      />
    </PageContainer>
  );
}
